import { Bell, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useListNotificationTypesQuery } from '@/client/api/notifications/list-notification-types.query';
import { useUpdateNotificationTypeMutation } from '@/client/api/notifications/update-notification-type.mutation';
import { NotificationType } from '@/client/api/notifications/list-notification-types.query';

const CHANNEL_BADGE = 'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium';

const NotificationsAllPage = () => {
  useBreadcrumbs([
    { label: 'Communications', href: '/admin/communications/notifications' },
    { label: 'Notifications' },
    { label: 'All' },
  ]);

  const { data, isLoading, refetch } = useListNotificationTypesQuery();
  const types = data?.data?.notification_types || [];
  const updateMutation = useUpdateNotificationTypeMutation();

  const toggleActive = (type: NotificationType) => {
    updateMutation.mutate(
      { ntypid: type.ntypid, active: !type.active },
      {
        onSuccess: () => refetch(),
        onError: (error) => toast.error(error.message || 'Failed to update notification type'),
      }
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <Bell className="h-7 w-7 text-blue-700" />
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
        </div>
        <p className="ml-10 max-w-2xl text-sm text-gray-500">
          Every notification type the platform can send, and which channels are available for each.
        </p>
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">Total Types</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{types.length}</p>
        </div>
        <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">Active</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{types.filter(t => t.active).length}</p>
        </div>
        <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">Deactivated</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{types.filter(t => !t.active).length}</p>
        </div>
        <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">Categories</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">
            {new Set(types.map(t => t.category).filter(Boolean)).size}
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Channels</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Active</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {isLoading && (
                <tr><td colSpan={4} className="px-6 py-16 text-center text-sm text-gray-500">Loading...</td></tr>
              )}
              {!isLoading && types.length === 0 && (
                <tr><td colSpan={4} className="px-6 py-16 text-center text-sm text-gray-500">No notification types found</td></tr>
              )}
              {types.map((type) => (
                <tr key={type.ntypid} className={`hover:bg-gray-50 ${!type.active ? 'opacity-60' : ''}`}>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{type.name}</div>
                    <div className="mt-0.5 text-xs text-gray-500 font-mono">{type.name_system}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 capitalize">{type.category || '—'}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {type.allow_in_app && <span className={`${CHANNEL_BADGE} bg-blue-50 text-blue-700`}>In-app</span>}
                      {type.allow_email && <span className={`${CHANNEL_BADGE} bg-emerald-50 text-emerald-700`}>Email</span>}
                      {type.allow_sms && <span className={`${CHANNEL_BADGE} bg-amber-50 text-amber-800`}>SMS</span>}
                      {!type.allow_in_app && !type.allow_email && !type.allow_sms && (
                        <span className="text-xs text-gray-400">None</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => toggleActive(type)}
                      disabled={updateMutation.isPending}
                      title={type.active ? 'Deactivate' : 'Activate'}
                      className="disabled:opacity-50"
                    >
                      {type.active
                        ? <ToggleRight className="w-6 h-6 text-green-500 ml-auto" />
                        : <ToggleLeft className="w-6 h-6 text-gray-400 ml-auto" />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NotificationsAllPage;
